import React from 'react';
import { fmtNum } from './calculations';
import CheckboxDropdown from './CheckboxDropdown';

// ═══════════════════════════════════════════════════════════
// FILTER BAR — Search, checkbox dropdowns, range inputs, sort
// CheckboxDropdown is now in its own shared file (used by MW + Positions)
// ═══════════════════════════════════════════════════════════
// Checkbox dropdown filters have moved to column headers in DataTable.jsx.
// FilterBar now only has: search, range inputs (S/S%, OI, Volume), sort controls, count.
function FilterBar({
  themeCtx, search, setSearch,
  moneyFilter, setMoneyFilter,
  ssMin, setSSMin, ssMax, setSSMax,
  oiMin, setOiMin, volMin, setVolMin,
  resultDateFrom, setResultDateFrom, resultDateTo, setResultDateTo,
  sortCol, setSortCol, sortDir, setSortDir, columns,
  rowCount, onClearAll, hasActiveFilters, onResort,
  positionFilter = 'all', setPositionFilter,
  bestPerUnderlying = false, setBestPerUnderlying,
  colsButton = null, // optional render slot — MW passes <ColumnsButton/> (shared ⚙ Columns control)
  downloadButton = null // optional render slot — MW passes the ↓ CSV download button
}) {
  const { t } = themeCtx;

  const inputStyle = { padding: '5px 4px', fontSize: 11, background: t.bgInput, border: `1px solid ${t.borderInput}`, borderRadius: 4, color: t.text, outline: 'none', textAlign: 'center' };
  const selectStyle = { padding: '5px 6px', fontSize: 11, background: t.bgInput, border: `1px solid ${t.borderInput}`, borderRadius: 4, color: t.textMuted, cursor: 'pointer' };

  // Session 46 — 3-state position-filter cycle. Click to cycle: all → with → without → all.
  // Visual: button shows the CURRENT state, color-coded.
  const cyclePositionFilter = () => {
    if (!setPositionFilter) return;
    if (positionFilter === 'all') setPositionFilter('with');
    else if (positionFilter === 'with') setPositionFilter('without');
    else setPositionFilter('all');
  };
  const positionFilterLabel =
    positionFilter === 'with' ? '✓ With Positions' :
    positionFilter === 'without' ? '✗ Without Positions' :
    'All Positions';
  const positionFilterColor =
    positionFilter === 'with' ? { bg: 'rgba(34,197,94,0.18)', fg: '#22c55e', border: 'rgba(34,197,94,0.4)' } :
    positionFilter === 'without' ? { bg: 'rgba(148,163,184,0.18)', fg: '#94a3b8', border: 'rgba(148,163,184,0.4)' } :
    { bg: t.bgInput, fg: t.textMuted, border: t.borderInput };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 16px', borderBottom: `1px solid ${t.border}`, background: t.headerBg, flexWrap: 'wrap', flexShrink: 0 }}>
      <input type="text" placeholder="Search: reliance pe 1200 feb..." value={search} onChange={e => setSearch(e.target.value)}
        style={{ width: 240, padding: '5px 10px', fontSize: 11.5, background: t.bgInput, border: `1px solid ${t.borderInput}`, borderRadius: 4, color: t.text, outline: 'none' }}
      />

      <CheckboxDropdown label="ITM/OTM" selected={moneyFilter} setSelected={setMoneyFilter} options={['All', 'ITM', 'OTM', 'ATM']} t={t} />

      <span style={{ fontSize: 11, color: t.textMuted }}>S/S%:</span>
      <input type="number" placeholder="Min" value={ssMin} onChange={e => setSSMin(e.target.value)} style={{ ...inputStyle, width: 50 }} />
      <span style={{ fontSize: 11, color: t.textDim }}>–</span>
      <input type="number" placeholder="Max" value={ssMax} onChange={e => setSSMax(e.target.value)} style={{ ...inputStyle, width: 50 }} />

      <span style={{ fontSize: 11, color: t.textMuted, marginLeft: 4 }}>OI (lots) ≥</span>
      <input type="number" placeholder="Min" value={oiMin} onChange={e => setOiMin(e.target.value)} style={{ ...inputStyle, width: 60 }} />
      <span style={{ fontSize: 11, color: t.textMuted, marginLeft: 4 }}>Vol (lots) ≥</span>
      <input type="number" placeholder="Min" value={volMin} onChange={e => setVolMin(e.target.value)} style={{ ...inputStyle, width: 60 }} />

      <span style={{ fontSize: 11, color: t.textMuted, marginLeft: 8 }}>Result:</span>
      <input type="date" value={resultDateFrom} onChange={e => setResultDateFrom(e.target.value)} style={{ ...inputStyle, width: 120 }} title="Result date from" />
      <span style={{ fontSize: 11, color: t.textDim }}>–</span>
      <input type="date" value={resultDateTo} onChange={e => setResultDateTo(e.target.value)} style={{ ...inputStyle, width: 120 }} title="Result date to" />

      <span style={{ fontSize: 11, color: t.textMuted, marginLeft: 8 }}>Sort:</span>
      <select value={sortCol} onChange={e => setSortCol(e.target.value)} style={selectStyle}>
        {columns.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
      </select>
      <button onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')}
        style={{ background: t.bgInput, border: `1px solid ${t.borderInput}`, borderRadius: 4, padding: '4px 8px', fontSize: 11, color: t.textMuted, cursor: 'pointer' }}
      >
        {sortDir === 'asc' ? '↑ Asc' : '↓ Desc'}
      </button>
      {onResort && (
        <button onClick={onResort}
          style={{ padding: '4px 10px', fontSize: 11, fontWeight: 600, background: 'rgba(59,130,246,0.15)', color: '#3b82f6', border: '1px solid rgba(59,130,246,0.3)', borderRadius: 4, cursor: 'pointer' }}
          title="Re-sort rows with latest data (auto every 60s)"
        >
          ↻ Re-sort
        </button>
      )}
      {colsButton}
      {downloadButton}

      {/* Session 46 — Position filter switch (3-state cycle: All → With → Without) */}
      {setPositionFilter && (
        <button onClick={cyclePositionFilter}
          style={{
            padding: '4px 10px', fontSize: 11, fontWeight: 600,
            background: positionFilterColor.bg, color: positionFilterColor.fg,
            border: `1px solid ${positionFilterColor.border}`, borderRadius: 4, cursor: 'pointer'
          }}
          title="Filter rows by whether you have an open position in the underlying. Click to cycle: All → With Positions → Without Positions."
        >
          {positionFilterLabel}
        </button>
      )}

      {/* Session 46 — Best-per-underlying-per-type toggle */}
      {setBestPerUnderlying && (
        <button onClick={() => setBestPerUnderlying(!bestPerUnderlying)}
          style={{
            padding: '4px 10px', fontSize: 11, fontWeight: 600,
            background: bestPerUnderlying ? 'rgba(234,179,8,0.18)' : t.bgInput,
            color: bestPerUnderlying ? '#eab308' : t.textMuted,
            border: `1px solid ${bestPerUnderlying ? 'rgba(234,179,8,0.4)' : t.borderInput}`,
            borderRadius: 4, cursor: 'pointer'
          }}
          title="When ON, after all filters, keep only the row with the highest annualized return per (stock, CE/PE) combination — at most 2 rows per stock."
        >
          {bestPerUnderlying ? '★ Best/Stock ON' : '☆ Best/Stock'}
        </button>
      )}
      {hasActiveFilters && (
        <button onClick={onClearAll}
          style={{ padding: '3px 10px', fontSize: 10, fontWeight: 600, background: 'rgba(239,68,68,0.15)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 4, cursor: 'pointer', marginLeft: 4 }}
        >
          Clear All Filters
        </button>
      )}
      <span style={{ fontSize: 11, color: t.textMuted, marginLeft: 'auto' }}>
        {fmtNum(rowCount)} options
      </span>
    </div>
  );
}

export default FilterBar;
